import { TextField } from '@mui/material';
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  ReactNode,
  useEffect,
} from 'react';
import { useFormContext as useReactHookFormContext } from 'react-hook-form';
import { useSiloRealFormContext } from '../base/use-form-context';
import { useFormFieldStyles } from './use-form-field-styles';

import * as SDK from '@siloreal:shared:sdk';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type Props = {
  tid: string;
  name: string;
  required?: boolean;
  type?: 'text' | 'email' | 'password';
  label?: string;
  placeholder?: string;
  size?: 'medium' | 'large';
  adornment?: ReactNode;
  adornmentPosition?: 'start' | 'end';
  validator?: SDK.validator.form.ValidationFn;
};

export const text: FR<HTMLInputElement, Props> = forwardRef((props, ref) => {
  const { name = 'text', validator } = props;
  const { label = 'Texto', placeholder = '' } = props;
  const { type = 'text', required = false } = props;
  const { size, adornment, adornmentPosition = 'end' } = props;

  const { verify } = useSiloRealFormContext();
  useEffect(() => verify.uniqueness(name), []);

  const {
    register,
    formState: { errors },
  } = useReactHookFormContext();
  const hasError = Boolean(errors[name]);
  const errorMessage = errors[name]?.message as string | undefined;

  const fieldSx = useFormFieldStyles({ size });

  const ADORNMENT = {
    START: adornment && adornmentPosition === 'start' ? adornment : null,
    END: adornment && adornmentPosition === 'end' ? adornment : null,
  };

  return (
    <TextField
      inputRef={ref}
      data-testid={props.tid}
      type={type}
      required={required}
      size="small"
      label={label}
      placeholder={placeholder}
      sx={fieldSx}
      error={hasError}
      helperText={errorMessage}
      slotProps={{
        input: {
          style: { backgroundColor: '#fff' },
          startAdornment: ADORNMENT.START,
          endAdornment: ADORNMENT.END,
        },
      }}
      {...register(name, {
        validate: (value) => {
          if (!validator) return true;
          const result = validator(value, { required });
          return SDK.core.match.result(result, {
            ok: () => true,
            err: (msg) => msg,
          });
        },
      })}
    />
  );
});
